import { useRef, useState } from "react";
import { Camera, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Photo upload widget for an existing entity (paciente, cavalo, etc.)
 * entity: 'pacientes' | 'cavalos' | 'terapeutas' | 'responsaveis'
 */
export default function PhotoUploader({ entity, entityId, currentUrl, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const displayUrl = preview
    ? (preview.startsWith("http") ? preview : `${backendUrl}${preview}`)
    : null;

  const handlePick = () => inputRef.current?.click();

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!entityId) {
      toast.error("Salve o cadastro antes de enviar a foto.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx 5 MB).");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(
        `/uploads/${entity}/${entityId}/foto`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreview(data.foto_url);
      onUploaded?.(data.foto_url);
      toast.success("Foto atualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-24 w-24 rounded-full overflow-hidden bg-secondary border-2 border-border flex items-center justify-center shrink-0">
        {displayUrl ? (
          <img src={displayUrl} alt="foto" className="h-full w-full object-cover" />
        ) : (
          <Camera className="h-8 w-8 text-primary/60" strokeWidth={1.6} />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          className="hidden"
          onChange={handleChange}
          data-testid={`photo-input-${entity}`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePick}
          disabled={uploading || !entityId}
          data-testid={`photo-upload-btn-${entity}`}
        >
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? "Enviando…" : displayUrl ? "Trocar foto" : "Enviar foto"}
        </Button>
        {!entityId && (
          <p className="text-xs text-muted-foreground">Salve o cadastro para habilitar upload.</p>
        )}
      </div>
    </div>
  );
}
